<script setup lang="ts">
import type { Brand } from "@/model/backend/brand"
import { computed, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useShopAppConfig } from "../composables"
import { useArticleFilterStore } from "../store"
import type { ArticleThcFilter } from "../store/useArticleFilterStore"
import { useShopDialogs } from "../composables/useShopDialogs"

const route = useRoute()
const router = useRouter()

function attached(): void {
    redirectOnClick.value = false
    const brand = filterStore.brand
    selectType(filterStore.type ?? "cannabis")
    selectBrand(brand)
    redirectOnClick.value = route.name !== "articles"
}

function selectType(type: string): void {
    filterStore.type = type
    selectBrand(undefined)
}

function selectBrand(brandId: string | undefined): void {
    const newBrand = filterStore.brand === brandId ? undefined : brandId
    if (redirectOnClick.value) {
        if (newBrand == null && brandId != null) {
            // clicked on same as already active but we are not on the articles page -> do not update filter
        } else {
            filterStore.brand = newBrand
        }
        router.push({ name: "articles" })
        return
    }

    filterStore.brand = newBrand
}

function selectSpecies(species: string): void {
    filterStore.species = filterStore.species === species ? undefined : species

    if (redirectOnClick.value) {
        router.push({ name: "articles" })
    }
}

/*─────────────────────────────────────┐
│   composables                        │
└─────────────────────────────────────*/
const { appConfig } = useShopAppConfig()
const filterStore = useArticleFilterStore()
const dialogs = useShopDialogs()
/*─────────────────────────────────────┐
│   refs                               │
└─────────────────────────────────────*/
const brands = computed(() => {
    const existingBrandIds: string[] = []
    const brands: Brand[] = []

    for (const article of appConfig.value.articles) {
        if (article.brand?.id && !existingBrandIds.includes(article.brand.id)) {
            existingBrandIds.push(article.brand.id)
            brands.push(article.brand)
        }
    }

    brands.sort((a: Brand, b: Brand) => {
        if ((a.name ?? "") < (b.name ?? "")) {
            return -1
        }
        if ((a.name ?? "") > (b.name ?? "")) {
            return 1
        }

        return 0
    })

    return brands
})

const species = computed(() => {
    const species: string[] = []

    for (const article of appConfig.value.articles) {
        if (!species.includes(article.species ?? "") && article.species) {
            species.push(article.species)
        }
    }

    return species.sort()
})

const redirectOnClick = ref(false)
/*─────────────────────────────────────┐
│   setup                              │
└─────────────────────────────────────*/
onMounted(() => attached())

/*─────────────────────────────────────┐
│   THC filter                         │
└─────────────────────────────────────*/
const thcFilters = [
    {
        name: "< 15%",
        min: 0,
        max: 15,
    },
    {
        name: "15% - 20%",
        min: 15,
        max: 20,
    },
    {
        name: "20% - 25%",
        min: 20,
        max: 25,
    },
    {
        name: "> 25%",
        min: 25,
        max: 999999,
    },
]

function selectThcFilter(filter: ArticleThcFilter): void {
    filterStore.thc = filterStore.thc?.name === filter.name ? undefined : filter

    if (redirectOnClick.value) {
        router.push({ name: "articles" })
    }
}

/*─────────────────────────────────────┐
│   price filter                       │
└─────────────────────────────────────*/
const priceFilters = [
    {
        name: "Blue",
        value: "blue",
    },
    {
        name: "Green",
        value: "green",
    },
    {
        name: "Yellow",
        value: "yellow",
    },
    {
        name: "Purple",
        value: "purple",
    },
]

function selectPriceFilter(filter: (typeof priceFilters)[number]): void {
    filterStore.priceCategory = filterStore.priceCategory === filter.value ? undefined : filter.value

    if (redirectOnClick.value) {
        router.push({ name: "articles" })
    }
}

/*─────────────────────────────────────┐
│   Terpene filter                     │
└─────────────────────────────────────*/
const terpenes = computed(() => {
    const terpenes: string[] = []

    for (const article of appConfig.value.articles) {
        article.terpeneProfile?.forEach((terpene: string) => {
            if (!terpenes.includes(terpene)) {
                terpenes.push(terpene)
            }
        })
    }

    return terpenes.sort()
})

function selectTerpene(terpene: string): void {
    if (filterStore.terpenes.includes(terpene)) {
        // if terpene is already active, remove it from the filter
        filterStore.terpenes = filterStore.terpenes.filter(t => t !== terpene)
    } else {
        // terpene is not selected yet

        // check that there are not more than 3 terpenes selected
        if (filterStore.terpenes.length >= 3) {
            dialogs.alert(
                "Achtung",
                "Es können maximal 3 Terpene ausgewählt werden. Bitte entferne zuerst ein Terpen aus deinem Filter.",
            )
            return
        }

        // if terpene is not filtered yet, add it to the filter
        filterStore.terpenes.push(terpene)
    }

    if (redirectOnClick.value) {
        router.push({ name: "articles" })
    }
}

const isBrandsOpen = ref(true)
const isProductOpen = ref(true)
const isSpeciesOpen = ref(true)
const isThcOpen = ref(true)
const isPriceOpen = ref(true)
const isTerpeneOpen = ref(true)
try {
    // If on mobile, close all filters by default.
    if (window.matchMedia("(max-width: 768px)").matches) {
        isBrandsOpen.value = false
        isProductOpen.value = false
        isSpeciesOpen.value = false
        isThcOpen.value = false
        isPriceOpen.value = false
        isTerpeneOpen.value = false
    }
} catch {
    // no-op: do nothing if window.matchMedia is not available
}
</script>

<template>
    <nav ref="sidebar" tabindex="-1" class="product-filter-sidebar">
        <section class="product-filter" aria-labelledby="filter-produkte">
            <div id="filter-produkte" class="product-filter-name" role="button" @click="isProductOpen = !isProductOpen">
                <i class="fa fa-fw fa-caret-right arrow" :class="{ open: isProductOpen }"></i>
                Produkte
            </div>
            <transition name="collapse">
                <ul v-show="isProductOpen" class="product-filter-items">
                    <li>
                        <a
                            v-if="appConfig.availableArticleTypes.includes('cannabis')"
                            :class="{ active: filterStore.type === 'cannabis' }"
                            tabindex="0"
                            @click.prevent="selectType('cannabis')"
                            @keyup.enter="selectType('cannabis')"
                        >
                            Blüten
                        </a>
                    </li>
                    <li>
                        <a
                            v-if="appConfig.availableArticleTypes.includes('essence')"
                            :class="{ active: filterStore.type === 'essence' }"
                            tabindex="0"
                            @click.prevent="selectType('essence')"
                            @keyup.enter="selectType('essence')"
                        >
                            Extrakte
                        </a>
                    </li>
                    <li>
                        <a
                            v-if="appConfig.availableArticleTypes.includes('xtracts')"
                            :class="{ active: filterStore.type === 'xtracts' }"
                            tabindex="0"
                            @click.prevent="selectType('xtracts')"
                            @keyup.enter="selectType('xtracts')"
                        >
                            CANN24 Xtract
                        </a>
                    </li>
                    <li>
                        <a
                            v-if="appConfig.availableArticleTypes.includes('readymade')"
                            :class="{ active: filterStore.type === 'readymade' }"
                            tabindex="0"
                            @click.prevent="selectType('readymade')"
                            @keyup.enter="selectType('readymade')"
                        >
                            CANN24 Readymade
                        </a>
                    </li>
                    <li>
                        <a
                            v-if="appConfig.availableArticleTypes.includes('milled')"
                            :class="{ active: filterStore.type === 'milled' }"
                            tabindex="0"
                            @click.prevent="selectType('milled')"
                            @keyup.enter="selectType('milled')"
                        >
                            CANN24 Milled
                        </a>
                    </li>
                    <li>
                        <a
                            v-if="appConfig.availableArticleTypes.includes('smalls')"
                            :class="{ active: filterStore.type === 'smalls' }"
                            tabindex="0"
                            @click.prevent="selectType('smalls')"
                            @keyup.enter="selectType('smalls')"
                        >
                            CANN24 Smalls
                        </a>
                    </li>
                    <li>
                        <a
                            v-if="appConfig.user && appConfig.availableArticleTypes.includes('standard')"
                            :class="{ active: filterStore.type === 'standard' }"
                            tabindex="0"
                            @click.prevent="selectType('standard')"
                            @keyup.enter="selectType('standard')"
                        >
                            Zubehör
                        </a>
                    </li>
                    <li>
                        <a
                            v-if="
                                appConfig.user &&
                                appConfig.user.payType !== 'doctor' &&
                                appConfig.availableArticleTypes.includes('merch')
                            "
                            :class="{ active: filterStore.type === 'merch' }"
                            tabindex="0"
                            @click.prevent="selectType('merch')"
                            @keyup.enter="selectType('merch')"
                        >
                            Merch
                        </a>
                    </li>
                </ul>
            </transition>
        </section>
        <section class="product-filter" aria-labelledby="filter-marke">
            <div id="filter-marke" class="product-filter-name" role="button" @click="isBrandsOpen = !isBrandsOpen">
                <i class="fa fa-fw fa-caret-right arrow" :class="{ open: isBrandsOpen }"></i>
                Marke
            </div>
            <transition name="collapse">
                <ul v-show="isBrandsOpen" class="product-filter-items">
                    <li>
                        <a
                            :class="{ active: filterStore.brand === null }"
                            tabindex="0"
                            @click.prevent="selectBrand(undefined)"
                            @keyup.enter="selectBrand(undefined)"
                            >Alle</a
                        >
                    </li>
                    <li v-for="brand in brands" :key="brand.id">
                        <a
                            :class="{ active: filterStore.brand === brand.id }"
                            tabindex="0"
                            @click.prevent="selectBrand(brand.id)"
                            @keyup.enter="selectBrand(brand.id)"
                        >
                            {{ brand.name }}
                        </a>
                    </li>
                </ul>
            </transition>
        </section>

        <section v-if="species.length > 0" aria-labelledby="filter-genetik" class="product-filter">
            <div id="filter-genetik" class="product-filter-name" role="button" @click="isSpeciesOpen = !isSpeciesOpen">
                <i class="fa fa-fw fa-caret-right arrow" :class="{ open: isSpeciesOpen }"></i>
                Genetik
            </div>
            <transition name="collapse">
                <ul v-show="isSpeciesOpen" class="product-filter-items">
                    <li v-for="specie in species" :key="specie">
                        <a
                            :class="{ active: filterStore.species === specie }"
                            tabindex="0"
                            @click.prevent="selectSpecies(specie)"
                            @keyup.enter="selectSpecies(specie)"
                        >
                            {{ specie }}
                        </a>
                    </li>
                </ul>
            </transition>
        </section>

        <section aria-labelledby="filter-thc" class="product-filter">
            <div id="filter-thc" class="product-filter-name" role="button" @click="isThcOpen = !isThcOpen">
                <i class="fa fa-fw fa-caret-right arrow" :class="{ open: isThcOpen }"></i>
                THC Gehalt
            </div>
            <transition name="collapse">
                <ul v-show="isThcOpen" class="product-filter-items">
                    <li v-for="filter in thcFilters" :key="filter.name">
                        <a
                            :class="{ active: filterStore.thc?.name === filter.name }"
                            tabindex="0"
                            @click.prevent="selectThcFilter(filter)"
                            @keyup.enter="selectThcFilter(filter)"
                        >
                            {{ filter.name }}
                        </a>
                    </li>
                </ul>
            </transition>
        </section>

        <section v-if="appConfig.showPrices" aria-labelledby="filter-preisgruppe" class="product-filter">
            <div id="filter-preisgruppe" class="product-filter-name" role="button" @click="isPriceOpen = !isPriceOpen">
                <i class="fa fa-fw fa-caret-right arrow" :class="{ open: isPriceOpen }"></i>
                Preisgruppe
            </div>
            <transition name="collapse">
                <ul v-show="isPriceOpen" class="product-filter-items">
                    <li v-for="filter in priceFilters" :key="filter.name">
                        <a
                            :class="{ active: filterStore.priceCategory === filter.value }"
                            tabindex="0"
                            @click.prevent="selectPriceFilter(filter)"
                            @keyup.enter="selectPriceFilter(filter)"
                        >
                            {{ filter.name }}
                        </a>
                    </li>
                </ul>
            </transition>
        </section>

        <section v-if="appConfig.user" aria-labelledby="filter-terpene" class="product-filter">
            <div id="filter-terpene" class="product-filter-name" role="button" @click="isTerpeneOpen = !isTerpeneOpen">
                <i class="fa fa-fw fa-caret-right arrow" :class="{ open: isTerpeneOpen }"></i>
                Terpene
            </div>
            <transition name="collapse">
                <ul v-show="isTerpeneOpen" class="product-filter-items">
                    <li v-for="terpene of terpenes" :key="terpene">
                        <a
                            :class="{ active: filterStore.terpenes.includes(terpene) }"
                            tabindex="0"
                            @click.prevent="selectTerpene(terpene)"
                            @keyup.enter="selectTerpene(terpene)"
                        >
                            {{ terpene }}
                        </a>
                    </li>
                </ul>
            </transition>
        </section>
    </nav>
</template>

<style scoped>
.collapse-body {
    overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
    opacity: 0;
}
.collapse-enter-active,
.collapse-leave-active {
    transition: all 300ms ease-in-out;
}
.arrow {
    display: inline-block;
    transition: transform 200ms ease;
    transform: rotate(0deg);
}
.arrow.open {
    transform: rotate(90deg);
}
</style>
